// components/ProfileTabs/ProfileTabs.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Message from "../LoadingError/Error";
import Toast from "./../LoadingError/Toast";
import Loading from "../LoadingError/Loading";
import { toast } from "react-toastify";
import { updateUserProfile } from "../../Redux/Actions/userActions";
import URL from "../../Redux/Url";
import SavedCardSection from "./Savedcardsection";
import "./ProfileTabs.css";
import "./updatePasswordform.css";
import { CustomSelect as ProfileCustomSelect} from "../../screens/typeheadselect";
import { COUNTRY_DATA } from "../../screens/countrydata";
import { usePostalLookup } from "../../screens/usepostallookup";

const ProfileTabs = () => {
  const [name, setName]                   = useState("");
  const [email, setEmail]                 = useState("");
  const [username, setUsername]           = useState("");
  const [password, setPassword]           = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [shippingAddress, setShippingAddress] = useState(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressForm, setAddressForm]     = useState({
    street1: "",
    city: "",
    state: "",
    zipcode: "",
    country: "US"
  });
  const [autoFilled, setAutoFilled] = useState(false);

  const [isSubscribed, setIsSubscribed]         = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [checkingStatus, setCheckingStatus]     = useState(true);

  const toastId = React.useRef(null);

  const Toastobjects = {
    pauseOnFocusLoss: false,
    draggable: false,
    pauseOnHover: false,
    autoClose: 2000,
  };

  const dispatch = useDispatch();

  const userDetails       = useSelector((state) => state.userDetails);
  const { loading, error, user } = userDetails;

  const userLogin         = useSelector((state) => state.userLogin);
  const { userInfo }      = userLogin;

  const userUpdateProfile = useSelector((state) => state.userUpdateProfile);
  const { loading: updateLoading } = userUpdateProfile;

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setUsername(user.username || "");
    }
  }, [user]);

  // Fetch shipping address from customer profile
  useEffect(() => {
    const fetchShippingAddress = async () => {
      if (userInfo && userInfo.token) {
        try {
          const res = await fetch(`${URL}/api/customers/profile`, {
            headers: { Authorization: `Bearer ${userInfo.token}` },
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.message || "Failed to fetch profile");
          setShippingAddress(data.shippingAddress);
          if (data.shippingAddress) {
            setAddressForm({
              street1: data.shippingAddress.street1 || "",
              city:    data.shippingAddress.city    || "",
              state:   data.shippingAddress.state   || "",
              zipcode: data.shippingAddress.zipcode || "",
              country: data.shippingAddress.country || "US"
            });
          }
        } catch (error) {
          console.error("Error fetching shipping address:", error.message);
        }
      }
    };
    fetchShippingAddress();
  }, [userInfo]);

  // Check newsletter subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (userInfo && userInfo.token) {
        try {
          setCheckingStatus(true);
          const res = await fetch(`${URL}/api/subscribers/status`, {
            headers: { Authorization: `Bearer ${userInfo.token}` },
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.message || "Failed to check subscription");
          setIsSubscribed(data.isSubscribed);
        } catch (error) {
          console.error("Error checking subscription status:", error.message);
        } finally {
          setCheckingStatus(false);
        }
      }
    };
    checkSubscriptionStatus();
  }, [userInfo]);

  const submitHandler = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error("Password does not match", Toastobjects);
      }
    } else {
      dispatch(updateUserProfile({ id: user._id, username, name, email, password }));
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.success("Profile Updated", Toastobjects);
      }
    }
  };

  const handleAddressChange = (e) => {
    setAddressForm({ ...addressForm, [e.target.name]: e.target.value });
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${URL}/api/customers/shipping-address`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({ shippingAddress: addressForm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update address");
      setShippingAddress(data.shippingAddress);
      setIsEditingAddress(false);
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.success("Address Updated Successfully", Toastobjects);
      }
    } catch (error) {
      console.error("Error updating address:", error.message);
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error("Failed to update address", Toastobjects);
      }
    }
  };

  const handleEditAddress  = () => setIsEditingAddress(true);
  const handleCancelEdit   = () => {
    setIsEditingAddress(false);
    setAutoFilled(false);
    if (shippingAddress) {
      setAddressForm({
        street1: shippingAddress.street1 || "",
        city:    shippingAddress.city    || "",
        state:   shippingAddress.state   || "",
        zipcode: shippingAddress.zipcode || "",
        country: shippingAddress.country || "US"
      });
    }
  };

  const zipStatus = usePostalLookup({
    zipcode: addressForm.zipcode,
    country: addressForm.country,
    onResult: ({ city: c, state: s }) => {
      setAddressForm((prev) => ({ ...prev, city: c, state: s }));
      setAutoFilled(true);
    },
  });

  const handleToggleSubscription = async () => {
    if (!userInfo || !userInfo.token) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error("Please login to manage newsletter subscription", Toastobjects);
      }
      return;
    }
    setLoadingSubscription(true);
    try {
      const res = await fetch(`${URL}/api/subscribers/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update subscription");
      setIsSubscribed(data.isSubscribed);
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.success(data.message, Toastobjects);
      }
    } catch (error) {
      console.error("Error toggling subscription:", error.message);
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error(error.message || "Failed to update subscription", Toastobjects);
      }
    } finally {
      setLoadingSubscription(false);
    }
  };

  return (
    <>
      <Toast />
      {error        && <Message variant="alert-danger">{error}</Message>}
      {loading      && <Loading />}
      {updateLoading && <Loading />}

      {/* ── Shipping Address ─────────────────────────────────────────────── */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="shipping-address-card">
            <div className="address-card-header">
              <div className="header-content">
                <div className="header-icon-wrapper">
                  <i className="fas fa-shipping-fast"></i>
                </div>
                <div>
                  <h5 className="mb-0">Shipping Address</h5>
                  <p className="header-subtitle">Manage your delivery information</p>
                </div>
              </div>
              {!isEditingAddress && shippingAddress && (
                <button
                  type="button"
                  className="btn-edit-address"
                  onClick={handleEditAddress}
                >
                  <i className="fas fa-edit"></i>
                  <span>Edit</span>
                </button>
              )}
            </div>

            <div className="address-card-body">
              {!shippingAddress && !isEditingAddress ? (
                <div className="no-address-section">
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <h6>No Shipping Address</h6>
                    <p>Add your shipping address to streamline your checkout process</p>
                    <button
                      type="button"
                      className="btn-add-address"
                      onClick={() => setIsEditingAddress(true)}
                    >
                      <i className="fas fa-plus"></i>
                      Add Shipping Address
                    </button>
                  </div>
                </div>
              ) : isEditingAddress ? (
                <form onSubmit={handleAddressSubmit} className="address-edit-form">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-group-modern">
                        <label htmlFor="street1">
                          <i className="fas fa-road"></i> Street Address
                        </label>
                        <input
                          type="text" className="form-control-modern"
                          id="street1" name="street1"
                          value={addressForm.street1} onChange={handleAddressChange}
                          required placeholder="123 Main Street"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-modern">
                        <label><i className="fas fa-flag"></i> Country</label>
                        <ProfileCustomSelect
                          options={Object.entries(COUNTRY_DATA).map(([code, data]) => ({
                            code,
                            name: data.name,
                            flag: data.flag,
                          }))}
                          value={addressForm.country}
                          onChange={(code) => {
                            setAddressForm((prev) => ({ ...prev, country: code, state: "", zipcode: "", city: "" }));
                            setAutoFilled(false);
                          }}
                          placeholder="Select country"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-modern">
                        <label htmlFor="zipcode">
                          <i className="fas fa-mail-bulk"></i>{" "}
                          {COUNTRY_DATA[addressForm.country]?.zipLabel || "Zip Code"}
                        </label>
                        <div className="zip-input-wrap">
                          <input
                            type="text" className="form-control-modern"
                            id="zipcode" name="zipcode"
                            value={addressForm.zipcode}
                            onChange={(e) => {
                              handleAddressChange(e);
                              setAutoFilled(false);
                            }}
                            required
                            placeholder={COUNTRY_DATA[addressForm.country]?.zipPlaceholder || "e.g. 90001"}
                          />
                          <span className="zip-status-icon">
                            {zipStatus === "loading"  && "⏳"}
                            {zipStatus === "found"    && "✅"}
                            {zipStatus === "notfound" && "❌"}
                          </span>
                        </div>
                        {autoFilled && zipStatus === "found" && (
                          <div className="zip-autofill-badge">
                            <span>✦</span> City &amp; state auto-filled
                          </div>
                        )}
                        {zipStatus === "notfound" && (
                          <div className="zip-notfound-badge">
                            ✕ Not found — fill manually
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-modern">
                        <label htmlFor="city"><i className="fas fa-city"></i> City</label>
                        <input
                          type="text" className="form-control-modern"
                          id="city" name="city"
                          value={addressForm.city}
                          onChange={(e) => { handleAddressChange(e); setAutoFilled(false); }}
                          required placeholder="Los Angeles"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-modern">
                        <label>
                          <i className="fas fa-map"></i>{" "}
                          {COUNTRY_DATA[addressForm.country]?.stateLabel || "State"}
                        </label>
                        <ProfileCustomSelect
                          options={
                            (COUNTRY_DATA[addressForm.country]?.states || COUNTRY_DATA.US.states).map(
                              (s) => ({ code: s.code, name: s.name })
                            )
                          }
                          value={addressForm.state}
                          onChange={(code) =>
                            setAddressForm((prev) => ({ ...prev, state: code }))
                          }
                          placeholder={`Select ${COUNTRY_DATA[addressForm.country]?.stateLabel || "State"}`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-save">
                      <i className="fas fa-check-circle"></i> Save Address
                    </button>
                    <button type="button" className="btn-cancel" onClick={handleCancelEdit}>
                      <i className="fas fa-times"></i> Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="address-display-grid">
                  {[
                    { icon: "fa-road",      label: "Street",   value: shippingAddress.street1 },
                    { icon: "fa-city",      label: "City",     value: shippingAddress.city    },
                    {
                      icon: "fa-map",
                      label: COUNTRY_DATA[shippingAddress.country]?.stateLabel || "State",
                      value: (() => {
                        const countryStates = COUNTRY_DATA[shippingAddress.country]?.states || [];
                        const found = countryStates.find((s) => s.code === shippingAddress.state);
                        return found ? found.name : shippingAddress.state;
                      })()
                    },
                    {
                      icon: "fa-mail-bulk",
                      label: COUNTRY_DATA[shippingAddress.country]?.zipLabel || "Zip Code",
                      value: shippingAddress.zipcode
                    },
                    {
                      icon: "fa-flag",
                      label: "Country",
                      value: COUNTRY_DATA[shippingAddress.country]
                        ? `${COUNTRY_DATA[shippingAddress.country].flag} ${COUNTRY_DATA[shippingAddress.country].name}`
                        : shippingAddress.country
                    },
                  ].map(({ icon, label, value }) => (
                    <div className="address-card-item" key={label}>
                      <div className="address-icon"><i className={`fas ${icon}`}></i></div>
                      <div className="address-content">
                        <span className="address-label">{label}</span>
                        <strong className="address-value">{value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Saved Payment Method ─────────────────────────────────────────── */}
      <SavedCardSection />

      {/* ── Newsletter Subscription ──────────────────────────────────────── */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="newsletter-subscription-card">
            {checkingStatus ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading newsletter preferences...</span>
              </div>
            ) : (
              <>
                <div className="newsletter-card-header">
                  <div className="header-content">
                    <div className="header-icon-wrapper newsletter-icon">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div>
                      <h5 className="mb-0">Newsletter Subscription</h5>
                      <p className="header-subtitle">
                        {isSubscribed
                          ? "You're subscribed to our newsletter"
                          : "Stay updated with our latest tips and news"}
                      </p>
                    </div>
                  </div>
                  <div className={`subscription-badge ${isSubscribed ? "active" : "inactive"}`}>
                    <i className={`fas ${isSubscribed ? "fa-check-circle" : "fa-times-circle"}`}></i>
                    <span>{isSubscribed ? "Subscribed" : "Not Subscribed"}</span>
                  </div>
                </div>

                <div className="newsletter-card-body">
                  <div className="newsletter-info">
                    {isSubscribed ? (
                      <>
                        <div className="info-item">
                          <i className="fas fa-paper-plane"></i>
                          <span>Receiving weekly tips and updates</span>
                        </div>
                        <div className="info-item">
                          <i className="fas fa-clock"></i>
                          <span>Delivered every Monday</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="info-item">
                          <i className="fas fa-lightbulb"></i>
                          <span>Get exclusive tips and insights</span>
                        </div>
                        <div className="info-item">
                          <i className="fas fa-gift"></i>
                          <span>Special offers for subscribers</span>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    className={`btn-newsletter-toggle ${isSubscribed ? "unsubscribe" : "subscribe"}`}
                    onClick={handleToggleSubscription}
                    disabled={loadingSubscription}
                  >
                    {loadingSubscription ? (
                      <><i className="fas fa-spinner fa-spin"></i><span>Processing...</span></>
                    ) : isSubscribed ? (
                      <><i className="fas fa-bell-slash"></i><span>Unsubscribe</span></>
                    ) : (
                      <><i className="fas fa-bell"></i><span>Subscribe Now</span></>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Profile Update Form ──────────────────────────────────────────── */}
      <form className="row form-container" onSubmit={submitHandler}>
        <div className="col-md-6">
          <div className="form">
            <label htmlFor="account-username">
              <i className="fas fa-user me-2"></i>Username
            </label>
            <input
              id="account-username"
              className="form-control"
              type="text"
              required
              placeholder="Enter username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form">
            <label htmlFor="account-email">
              <i className="fas fa-envelope me-2"></i>E-mail Address
            </label>
            <input
              id="account-email"
              className="form-control"
              type="email"
              required
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form">
            <label htmlFor="account-pass">
              <i className="fas fa-lock me-2"></i>New Password
            </label>
            <input
              id="account-pass"
              className="form-control"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form">
            <label htmlFor="account-confirm-pass">
              <i className="fas fa-lock me-2"></i>Confirm Password
            </label>
            <input
              id="account-confirm-pass"
              className="form-control"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
        </div>
        <div className="col-12">
          <button type="submit" className="update-btn">
            <i className="fas fa-save me-2"></i>Update Profile
          </button>
        </div>
      </form>
    </>
  );
};

export default ProfileTabs;